import {
  HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { SnackBarMessagesService } from './snack-bar-messages.service';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  translateServiceInjected!: TranslateService;

  constructor(
    private snackBarMessagesService: SnackBarMessagesService,
    private injector: Injector
  ) { }

  /**
   * Intercepts http errors and displays them to the user.
   */
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.translateServiceInjected = this.injector.get(TranslateService);

          const err = request.responseType === 'text' ? JSON.parse(error.error) : error.error;
          const errorCodes = err?.errorCodes;

          if ((error.status === 401 || err?.statusCode === 401 || err?.error === 'invalid_token') &&
            error.url !== `${environment.apiUrl}/identity`) {
            return throwError(() => error);
          }

          if (!errorCodes) {
            this.handleError(marker('errors.somethingWentWrong'));
            return throwError(() => error);
          }

          for (let code of errorCodes) {
            code = +code;

            switch (code) {
              case 100: this.handleError(marker('errors.companyNameMinLength')); break;
              case 101: this.handleError(marker('errors.noteMaxLength')); break;
              case 102: this.handleError(marker('errors.runInfoMaxLength')); break;

              case 201: this.handleError(marker('errors.emptyTitle')); break;
              case 202: this.handleError(marker('errors.titleMaxLength')); break;
              case 203: this.handleError(marker('errors.invalidLink')); break;

              case 400: this.handleError(marker('errors.usernameNotEmpty')); break;
              case 402: this.handleError(marker('errors.usernameExists')); break;
              case 403: this.handleError(marker('errors.usernameMaxLength')); break;
              case 405: this.handleError(marker('errors.passwordMinLength')); break;
              case 406: this.handleError(marker('errors.passwordMaxLength')); break;
              case 407: this.handleError(marker('errors.passwordMissingUpperCase')); break;
              case 408: this.handleError(marker('errors.passwordMissingLowerCase')); break;
              case 409: this.handleError(marker('errors.passwordMissingDigit')); break;
              case 410: this.handleError(marker('errors.passwordMissingSpecialCharacter')); break;
              case 411: this.handleError(marker('errors.passwordNotAllowedSpecialCharacter')); break;
              case 412: this.handleError(marker('errors.passwordNotAllowedSpecialWord')); break;
              case 413: this.handleError(marker('errors.passwordCannotContainUsername')); break;
              case 414: this.handleError(marker('errors.passwordTwoPasswordsDoesNotMatch')); break;
              case 415: this.handleError(marker('errors.emailInvalidEmail')); break;
              case 416: this.handleError(marker('errors.roleNotEmpty')); break;
              case 417: this.handleError(marker('errors.roleInvalidRole')); break;
              case 418: this.handleError(marker('errors.invalidCredentials')); break;
              case 420: this.handleError(marker('errors.nameOfUserNotEmpty')); break;
              case 421: this.handleError(marker('errors.nameOfUserMaxLength')); break;
              case 433: this.handleError(marker('errors.partnerLookupNotFound')); break;
              case 434: this.handleError(marker('errors.partnerLookupAlreadyExists')); break;

              case 600: this.handleError(marker('errors.deviceNameAlreadyExists')); break;
              case 601: this.handleError(marker('errors.deviceNameMaxLength')); break;

              case 700: this.handleError(marker('errors.pairRunsAlreadyPaired')); break;
              case 701: this.handleError(marker('errors.pairRunsNotLinkedDevices')); break;
              case 702: this.handleError(marker('errors.unpairRunsNotPairedWithEachOther')); break;

              case 901: this.handleError(marker('errors.runViewerAlreadyExists')); break;

              default: this.handleError(marker('errors.somethingWentWrong')); break;
            }
          }

          return throwError(() => error);
        })
      );
  }

  handleError(errorKey: string) {
    this.translateServiceInjected.get(errorKey).subscribe((translatedString: string) => {
      this.showError(translatedString);
    });
  }

  showError(errorMessage: string) {
    this.translateServiceInjected.get(marker('general.ok')).subscribe((translatedString: string) => {
      this.snackBarMessagesService.showErrorMessage(errorMessage, translatedString);
    });
  }
}
